<template>
    <aside class="phu-sidebar" :class="{ 'phu-sidebar--collapsed': collapsed }">
        <template v-if="!collapsed">
            <div class="phu-sidebar__date">
                <i class="i-andy-calendar phu-sidebar__date-icon" aria-hidden="true"></i>
                <span>{{ weekday }} | {{ formattedDate }}</span>
            </div>

            <header class="phu-sidebar__header">
                <h2 class="phu-sidebar__title">{{ $t('phu_production.sidebar.recommendations') }}</h2>
                <button type="button" class="phu-sidebar__add" :aria-label="$t('phu_production.sidebar.add_product')" @click="$emit('add-product')">
                    <i class="i-andy-add i-andy-s" aria-hidden="true"></i>
                </button>
            </header>

            <div class="phu-sidebar__list">
                <p v-if="recommendations.length === 0" class="phu-sidebar__empty">{{ $t('phu_production.sidebar.empty') }}</p>
                <RecommendationCard
                    v-for="recommendation in recommendations"
                    :key="recommendation.id"
                    :recommendation="recommendation"
                    :product="products[recommendation.product_id]"
                    :now="now"
                    @apply="$emit('apply', $event)"
                />
            </div>

            <CollapsiblePanel class="phu-sidebar__sales" :title="$t('phu_production.sidebar.sales_information')" :initially-open="false">
                <SalesSummary :summary="summary" />
            </CollapsiblePanel>
        </template>

        <footer class="phu-sidebar__footer">
            <button type="button" class="phu-sidebar__toggle" :aria-label="$t('phu_production.sidebar.toggle')" :aria-expanded="collapsed ? 'false' : 'true'" @click="$emit('toggle')">
                <i class="i-andy-m" :class="collapsed ? 'i-andy-chevron-right' : 'i-andy-chevron-left'" aria-hidden="true"></i>
            </button>
            <button v-if="!collapsed && configurable" type="button" class="phu-sidebar__settings" @click="$emit('settings')">
                {{ $t('phu_production.sidebar.settings') }}
            </button>
        </footer>
    </aside>
</template>

<script>
import RecommendationCard from './RecommendationCard.vue'
import CollapsiblePanel from '@/components/domain/ui/CollapsiblePanel.vue'
import SalesSummary from '@/components/domain/sales/salesSummary.vue'

export default {
    name: 'PhuSidebar',
    components: { RecommendationCard, CollapsiblePanel, SalesSummary },
    props: {
        recommendations: { type: Array, required: true },
        products: { type: Object, required: true },
        summary: { type: Object, default: () => ({}) },
        now: { type: Number, required: true },
        collapsed: { type: Boolean, default: false },
        configurable: { type: Boolean, default: true }
    },
    computed: {
        weekday() {
            return moment(this.now).locale(localStorage.language).format('ddd').toUpperCase()
        },
        formattedDate() {
            const shortYearFormat = moment.localeData(localStorage.language).longDateFormat('L').replace('YYYY', 'YY')
            return moment(this.now).format(shortYearFormat)
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-sidebar {
    @include display-flex();
    @include flex-direction(column);
    gap: $phu-gap;
    height: 100%;
    padding: 8px;
    border-radius: $phu-radius-s;
    background-color: $phu-surface;

    &__date {
        @include display-flex();
        @include align-items(center);
        gap: 8px;
        padding: 12px 8px;
        border-radius: $phu-radius-s;
        background-color: $phu-button;
        font-family: $text-bold;
        font-size: 1rem;
        text-transform: uppercase;
        color: $phu-primary;
    }

    &__date-icon {
        font-size: 1.25rem;
    }

    &__header {
        @include display-flex();
        @include align-items(center);
        @include justify-content(space-between);
        padding: 8px;
        border-radius: $phu-radius-s;
        background-color: $phu-section;
    }

    &__title {
        margin: 0;
        font-family: $text-bold;
        font-size: 1rem;
        line-height: 16px;
        color: $phu-text;
    }

    &__add {
        @include phu-touch-target();
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        padding: 8px;
        border: none;
        border-radius: $phu-radius-s;
        background-color: $phu-success;
        color: $color-white;
        cursor: pointer;

        i {
            color: $color-white;
        }
    }

    &__list {
        @include display-flex();
        @include flex-direction(column);
        flex: 1;
        gap: $phu-gap;
        min-height: 0;
        overflow-y: auto;
    }

    &__empty {
        margin: 0;
        font-family: $text;
        font-size: 0.875rem;
        text-align: center;
        color: $phu-text-muted;
    }

    &__footer {
        @include display-flex();
        @include align-items(center);
        gap: $phu-gap;
        margin-top: auto;
    }

    &__toggle,
    &__settings {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        min-height: 40px;
        padding: 8px;
        border: none;
        border-radius: $phu-radius-s;
        background-color: $phu-button;
        color: $phu-primary;
        cursor: pointer;
    }

    &__toggle {
        flex-shrink: 0;
    }

    &__settings {
        flex: 1;
        font-family: $text-bold;
        font-size: 1.25rem;
        line-height: 18px;
    }

    &--collapsed {
        padding: 12px 4px;
        background-color: transparent;
    }
}
</style>
